import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../apiClient';
import { auth } from '../../firebase';
import { writeAuditLog } from '../../lib/auditLogger';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ja } from 'date-fns/locale';
import "react-datepicker/dist/react-datepicker.css";
import { ChevronRight, X } from 'lucide-react';

registerLocale('ja', ja);

// --- 定数定義 (全件検索用 & 翻訳キー同期) ---
const COMMON_ACTIONS = [
    'member_login_success', 
    'invoice_csv_upload', 
    'invoice_deleted', 
    'customer_csv_upload', 
    'customer_delete', 
    'member_invited', 
    'member_removed', 
    'plan_changed',
    'account_canceled',
    'account_reactivated',
    'stripe_api_key_updated',
    'tax_rate_created',
    'tax_rate_updated',
    'tax_rate_deleted',
    'tenant_suspended',
    'tenant_resumed',
    'tenant_invoice_locked',
    'tenant_invoice_unlocked',
    'trial_settings_updated',
    'trial_started',
    'trial_extended',
    'trial_ended_scheduled',
    'refund_execute', 
    'credit_execute',
    'system_user_create',
    'system_user_update',
    'system_user_created',
    'system_user_updated',
    'system_user_disable',
    'system_user_disabled',
    'system_user_enable',
    'system_user_enabled'
];

const formatLabel = (key, t) => {
    const permKey = `permission_${key}`;
    const translated = t(permKey);
    if (translated && translated !== permKey) return translated;
    return t(key, key);
};

const formatVal = (val, t) => {
    if (val === true) return <span className="text-emerald-600 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">{t('enabled', '有効')}</span>;
    if (val === false) return <span className="text-gray-500 font-bold bg-gray-100 px-2 py-0.5 rounded border border-gray-200">{t('disabled', '無効')}</span>;
    if (val === null || val === undefined) return <span className="text-gray-400">-</span>;
    return String(val);
};

const DetailItem = ({ label, value }) => (
  <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
    <dt className="text-sm font-semibold text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 whitespace-pre-wrap break-words">{value}</dd>
  </div>
);

const DetailsObject = ({ data, t }) => {
    if (!data) return <span className="text-gray-400">（データなし）</span>;
    if (typeof data !== 'object' || data === null) return <span className="text-gray-900">{String(data)}</span>;

    if (Array.isArray(data.error)) {
        return (
            <div className="overflow-x-auto my-2">
                <table className="w-full text-xs border-collapse border border-gray-300" style={{tableLayout: 'fixed'}}>
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="border p-2 font-bold w-12 text-left">行</th>
                            <th className="border p-2 font-bold w-20 text-left">コード</th>
                            <th className="border p-2 font-bold text-left">内容</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.error.map((errorItem, index) => (
                            <tr key={index} className="bg-white">
                                <td className="border p-2 text-center text-gray-600">{errorItem.row || '-'}</td>
                                <td className="border p-2 font-mono text-red-600">{errorItem.code || '-'}</td>
                                <td className="border p-2 break-words text-gray-800">{errorItem.message}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    // メンバー権限更新 (from, to がオブジェクトとして含まれている場合) の差分テーブル表示
    if (data.from && typeof data.from === 'object' && data.to && typeof data.to === 'object') {
        const categories = Array.from(new Set([...Object.keys(data.from || {}), ...Object.keys(data.to || {})]));
        return (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white my-2">
                <table className="w-full text-xs border-collapse">
                    <thead className="bg-gray-100 text-gray-700 font-bold border-b border-gray-200">
                        <tr>
                            <th className="p-2.5 text-left w-2/5">{t('permissionItem', '権限項目')}</th>
                            <th className="p-2.5 text-left w-[30%] border-l border-gray-200 bg-red-50/50 text-red-900">{t('beforeChange', '変更前')}</th>
                            <th className="p-2.5 text-left w-[30%] border-l border-gray-200 bg-emerald-50/50 text-emerald-900">{t('afterChange', '変更後')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {categories.map((catKey) => {
                            const fromCat = data.from?.[catKey] || {};
                            const toCat = data.to?.[catKey] || {};
                            const permKeys = Array.from(new Set([...Object.keys(fromCat), ...Object.keys(toCat)]));
                            return (
                                <React.Fragment key={catKey}>
                                    <tr className="bg-gray-50/80 font-bold text-gray-800">
                                        <td colSpan="3" className="p-2">
                                            {formatLabel(catKey, t)}
                                        </td>
                                    </tr>
                                    {permKeys.map((pKey) => {
                                        const fromVal = fromCat[pKey];
                                        const toVal = toCat[pKey];
                                        const isDiff = fromVal !== toVal;
                                        return (
                                            <tr key={pKey} className={isDiff ? 'bg-amber-50/50' : 'hover:bg-gray-50/30'}>
                                                <td className="p-2 pl-4 text-gray-700 font-medium">
                                                    {formatLabel(pKey, t)}
                                                </td>
                                                <td className="p-2 border-l border-gray-100">
                                                    {formatVal(fromVal, t)}
                                                </td>
                                                <td className="p-2 border-l border-gray-100">
                                                    {formatVal(toVal, t)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    const renderValue = (val) => {
        if (typeof val === 'object' && val !== null) {
            return <div className="pl-4 mt-1 border-l-2 border-indigo-50 py-1"><DetailsObject data={val} t={t} /></div>;
        }
        return <span className="text-gray-900 font-medium">{String(val)}</span>;
    };
    
    return (
        <dl className="space-y-2">
            {Object.entries(data).map(([key, value]) => (
                <div key={key} className="sm:grid sm:grid-cols-3 sm:gap-2 border-b border-gray-50 pb-1 last:border-0">
                    <dt className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter self-start pt-1">{t(key, key)}</dt>
                    <dd className="sm:col-span-2">{renderValue(value)}</dd>
                </div>
            ))}
        </dl>
    );
};

const renderResultBadge = (result, t, isTable = false) => {
    const isSuccess = result === 'success';
    const label = isSuccess ? t('success') : t('failed');
    if (isTable) {
        return (
            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase ${isSuccess ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                {label}
            </span>
        );
    }
    return (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${isSuccess ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {label}
        </span>
    );
};

const LogDetailModal = ({ log, onClose, t }) => {
  if (!log) return null;
  const hasError = log.result === 'failed' || (log.details && (log.details.error || log.details.errorMessage || log.details.error_code));

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl transform transition-all border border-gray-200">
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50 rounded-t-xl">
          <h2 className="text-lg font-bold text-gray-800">ログ詳細</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded-full transition-colors text-gray-500"><X className="h-6 w-6" /></button>
        </div>
        <div className="p-6 space-y-6 overflow-y-auto max-h-[75vh]">
          <section>
            <h3 className="text-xs font-bold text-indigo-500 uppercase tracking-widest mb-3">基本情報</h3>
            <dl className="divide-y divide-gray-100 border-t border-b border-gray-100">
                <DetailItem label="日時" value={log.created_at.toLocaleString('ja-JP')} />
                <DetailItem label="顧客ID" value={<span className="font-mono bg-gray-100 px-1 rounded">{log.billwaveCustomerId}</span>} />
                <DetailItem label="実行者" value={
                    <div>
                        <div className="font-bold text-gray-800">{log.actor_name || 'システム'}</div>
                    </div>
                } />
                <DetailItem label="アクション" value={<span className="font-bold text-indigo-700">{t(log.action, log.action)}</span>} />
                <DetailItem label="ターゲット" value={log.target_name ? `${log.target_name} (ID: ${log.target_id || '-'})` : (log.target_id || '-')} />
                <DetailItem 
                    label="実行結果"
                    value={renderResultBadge(log.result, t, false)}
                />
            </dl>
          </section>
          <section className="bg-gray-50 p-4 rounded-lg border border-gray-100 shadow-inner">
            <h3 className={`text-xs font-bold uppercase tracking-widest mb-3 ${hasError ? 'text-red-500' : 'text-gray-400'}`}>{hasError ? 'エラー・詳細データ' : '詳細データ'}</h3>
            <div className="bg-white p-4 rounded-md border border-gray-200"><DetailsObject data={log.details} t={t} /></div>
          </section>
        </div>
        <div className="flex items-center justify-end p-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-6 py-2 bg-gray-800 text-white rounded-lg shadow-md hover:bg-black font-bold transition-all text-sm">閉じる</button>
        </div>
      </div>
    </div>
  );
};

const ActivityLog = () => {
  const { t } = useTranslation('activity');
  const location = useLocation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [lastDocId, setLastDocId] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const initialStartDate = useMemo(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); d.setHours(0, 0, 0, 0); return d; }, []);
  const [searchInputs, setSearchInputs] = useState({ billwaveCustomerId: '', actor_search: '', action: '', result: '', startDate: initialStartDate, endDate: new Date() });
  const [activeFilters, setActiveFilters] = useState({});
  const PAGE_SIZE = 50;

  const fetchLogs = useCallback(async (filters, isLoadMore = false, currentLastDocId = null) => {
    if (isLoadMore ? loadingMore : loading) return;
    isLoadMore ? setLoadingMore(true) : setLoading(true);
    setError('');
    try {
        const user = auth.currentUser;
        if (!user) throw new Error('User not authenticated');
        const token = await user.getIdToken();
        const params = { ...filters };
        if (filters.startDate instanceof Date) params.startDate = filters.startDate.toISOString();
        if (filters.endDate instanceof Date) params.endDate = filters.endDate.toISOString();
        const effectiveLastDocId = isLoadMore ? (currentLastDocId || lastDocId) : null;
        if (effectiveLastDocId) params.lastDocId = effectiveLastDocId;
        const queryString = new URLSearchParams(params).toString();
        const response = await apiClient.get(`/v1/activities/search?${queryString}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!response.ok) { const errorData = await response.json(); throw new Error(errorData.error || t('errorLoadingLogs')); }
        const data = await response.json();
        const formattedData = data.map(log => ({ ...log, created_at: log.created_at ? new Date(log.created_at._seconds * 1000) : new Date() }));
        setLogs(prev => isLoadMore ? [...prev, ...formattedData] : formattedData);
        setHasMore(data.length === PAGE_SIZE);
        if (data.length > 0) setLastDocId(data[data.length - 1].id);
    } catch (err) { console.error("Error fetching logs:", err); setError(err.message); } finally { setLoading(false); setLoadingMore(false); }
  }, [lastDocId, loading, loadingMore, t]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        const queryParams = new URLSearchParams(location.search);
        const customerIdFromUrl = queryParams.get('billwaveCustomerId') || '';
        const initFilters = { ...searchInputs, billwaveCustomerId: customerIdFromUrl };
        setSearchInputs(initFilters);
        const cleanFilters = Object.fromEntries(Object.entries(initFilters).filter(([_, value]) => value !== '' && value !== null));
        setActiveFilters(cleanFilters); setLogs([]); setLastDocId(null); fetchLogs(cleanFilters, false, null);
      }
    });
    return () => unsubscribe();
  }, [location.search]);

  const handleSearch = (e) => { if (e) e.preventDefault(); const cleanFilters = Object.fromEntries(Object.entries(searchInputs).filter(([_, value]) => value !== '' && value !== null)); setActiveFilters(cleanFilters); setLastDocId(null); setLogs([]); fetchLogs(cleanFilters, false, null); };
  const handleReset = () => { const reset = { billwaveCustomerId: '', actor_search: '', action: '', result: '', startDate: initialStartDate, endDate: new Date() }; setSearchInputs(reset); setActiveFilters(reset); setLastDocId(null); setLogs([]); fetchLogs(reset, false, null); };
  const handleInputChange = (e) => { const { name, value } = e.target; setSearchInputs(prev => ({ ...prev, [name]: value })); };
  const actionOptions = useMemo(() => ['', ...COMMON_ACTIONS].sort(), []);

  const handleOpenDetail = (log) => {
    setSelectedLog(log);
    if (log && log.id) {
      writeAuditLog({
        action: 'ACTIVITYLOG_VIEW',
        resourceType: 'activity_log',
        resourceId: log.id,
        metadata: { source: 'admin_console' }
      });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6"><h2 className="text-2xl font-bold text-gray-800 tracking-tight">アクティビティログ</h2></div>
      <form onSubmit={handleSearch} className="bg-white p-6 rounded-xl shadow-md mb-8 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">表示期間</label>
            <div className="flex items-center space-x-2">
              <DatePicker selected={searchInputs.startDate} onChange={(date) => setSearchInputs(prev => ({...prev, startDate: date}))} showTimeSelect dateFormat="yyyy/MM/dd HH:mm" locale="ja" className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 transition-all outline-none shadow-sm" />
              <span className="text-gray-300">〜</span>
              <DatePicker selected={searchInputs.endDate} onChange={(date) => setSearchInputs(prev => ({...prev, endDate: date}))} showTimeSelect dateFormat="yyyy/MM/dd HH:mm" locale="ja" className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 transition-all outline-none shadow-sm" />
            </div>
          </div>
          <div><label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">BillWave顧客ID</label><input type="text" name="billwaveCustomerId" value={searchInputs.billwaveCustomerId} onChange={handleInputChange} placeholder="IDを入力" className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 transition-all outline-none shadow-sm" /></div>
          <div><label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">実行者UID/キーワード</label><input type="text" name="actor_search" value={searchInputs.actor_search} onChange={handleInputChange} placeholder="UIDまたはメールアドレス" className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 transition-all outline-none shadow-sm" /></div>
          <div><label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">アクション</label><select name="action" value={searchInputs.action} onChange={handleInputChange} className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none shadow-sm"><option value="">すべて</option>{actionOptions.filter(Boolean).map(act => (<option key={act} value={act}>{t(act, act)}</option>))}</select></div>
          <div><label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">実行結果</label><select name="result" value={searchInputs.result} onChange={handleInputChange} className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none shadow-sm"><option value="">すべて</option><option value="success">成功 (success)</option><option value="failed">失敗 (failed)</option></select></div>
        </div>
        <div className="mt-6 flex justify-end gap-3 border-t border-gray-50 pt-4">
            <button type="submit" className="bg-indigo-600 text-white px-10 py-2 rounded-lg hover:bg-indigo-700 text-sm font-bold shadow-md shadow-indigo-100 transition-all">検索</button>
            <button type="button" onClick={handleReset} className="bg-gray-100 text-gray-600 px-6 py-2 rounded-lg hover:bg-gray-200 text-sm font-bold transition-all">リセット</button>
        </div>
      </form>
      {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6 border border-red-100 flex items-center gap-2 font-medium"><X size={18}/>{error}</div>}
      <div className="bg-white shadow-xl rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse table-auto">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="p-3 text-left whitespace-nowrap text-[10px] font-bold text-gray-400 uppercase tracking-widest">日時</th>
                <th className="p-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest">顧客ID</th>
                <th className="p-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest">実行者</th>
                <th className="p-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest">アクション</th>
                <th className="p-3 text-left whitespace-nowrap text-[10px] font-bold text-gray-400 uppercase tracking-widest">結果</th>
                <th className="p-3 text-left"><span className="sr-only">Details</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {logs.length === 0 && !loading ? (<tr><td colSpan="6" className="p-12 text-center text-gray-400 italic">条件に一致するアクティビティログが見つかりません。</td></tr>) : logs.map((log) => (
                <tr key={log.id} className="hover:bg-indigo-50/30 transition-colors group">
                    <td className="p-3 whitespace-nowrap text-xs text-gray-600 font-medium">{log.created_at.toLocaleString('ja-JP')}</td>
                    <td className="p-3 whitespace-nowrap text-xs text-gray-400 font-mono">{log.billwaveCustomerId}</td>
                    <td className="p-3 text-xs text-gray-700 break-all"><div className="font-bold text-gray-900">{log.actor_name || 'システム'}</div></td>
                    <td className="p-3 text-xs text-gray-700 break-all font-medium">{t(log.action, log.action)}</td>
                    <td className="p-3 whitespace-nowrap">{renderResultBadge(log.result, t, true)}</td>
                    <td className="p-3 text-center"><button onClick={() => handleOpenDetail(log)} className="text-indigo-400 group-hover:text-indigo-600 transition-all p-1.5 rounded-full hover:bg-white hover:shadow-sm" title="詳細を表示"><ChevronRight size={18}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {hasMore && (<div className="mt-10 flex justify-center"><button onClick={() => fetchLogs(activeFilters, true)} disabled={loadingMore} className="bg-white border border-gray-200 text-gray-700 px-12 py-2.5 rounded-xl hover:bg-indigo-600 hover:text-white hover:border-indigo-600 font-bold transition-all shadow-sm disabled:opacity-50 text-sm">{loadingMore ? 'データを読み込み中...' : 'さらに読み込む'}</button></div>)}
      {selectedLog && <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} t={t} />}
    </div>
  );
};

export default ActivityLog;
